import { useEffect, useMemo, useState, useRef } from "react";
import { Pencil, Save, X } from "lucide-react";
import styles from "./editable-date-cell.module.scss";

const pad2 = (n) => String(n).padStart(2, "0");

function defaultToInputValue(rawValue) {
	if (!rawValue) return "";
	const normalized = typeof rawValue === "string" ? rawValue.replace(" ", "T") : rawValue;
	const dateObj = new Date(normalized);
	if (isNaN(dateObj.getTime())) return "";
	const yyyy = dateObj.getFullYear();
	const mm = pad2(dateObj.getMonth() + 1);
	const dd = pad2(dateObj.getDate());
	const hh = pad2(dateObj.getHours());
	const mi = pad2(dateObj.getMinutes());
	return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function defaultFormatOutput(inputValue) {
	if (!inputValue) return null;
	const dateObj = new Date(inputValue);
	if (isNaN(dateObj.getTime())) return null;
	const yyyy = dateObj.getFullYear();
	const mm = pad2(dateObj.getMonth() + 1);
	const dd = pad2(dateObj.getDate());
	const hh = pad2(dateObj.getHours());
	const mi = pad2(dateObj.getMinutes());
	const ss = "00";
	return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

export default function EditableDateCell({
	value,
	row,
	fieldName = "created_at",
	onSave,
	getRecordId = (originalRow) => originalRow?.id,
	displayFormatter,
	toInputValue = defaultToInputValue,
	formatOutput = defaultFormatOutput,
	autoSaveOnBlur = false,
	disabled = false,
	className = "",
	readButtonTitle = "Editar fecha",
	savingText = "Guardando…",
	cancelText = "Cancelar",
	saveText = "Guardar",
	expandOnEdit = true,
}) {
	const recordId = useMemo(() => getRecordId?.(row?.original), [getRecordId, row]);
	const [isEditing, setIsEditing] = useState(false);
	const [inputValue, setInputValue] = useState(toInputValue(value));
	const [isSaving, setIsSaving] = useState(false);
	const [errorText, setErrorText] = useState("");

	const inputRef = useRef(null);
	const isMountedRef = useRef(false);
	const abortRef = useRef(null);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			if (abortRef.current) {
				try {
					abortRef.current.abort();
				} catch {
					// Ignore abort errors
				}
			}
		};
	}, []);

	useEffect(() => {
		if (!isEditing) setInputValue(toInputValue(value));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	useEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, [isEditing]);

	const beginEdit = () => {
		if (disabled || isSaving) return;
		setErrorText("");
		setIsEditing(true);
	};

	const cancelEdit = () => {
		setInputValue(toInputValue(value));
		setErrorText("");
		setIsEditing(false);
	};

	const persistChange = async () => {
		if (disabled || isSaving) return;
		setErrorText("");

		const newValue = formatOutput(inputValue);
		if (!newValue || !recordId || typeof onSave !== "function") {
			cancelEdit();
			return;
		}

		const controller = new AbortController();
		abortRef.current = controller;

		try {
			setIsSaving(true);
			await onSave({
				recordId,
				originalRow: row?.original,
				fieldName,
				newValue,
				inputValue,
				signal: controller.signal,
			});
			if (!isMountedRef.current) return;
			setIsEditing(false);
		} catch (err) {
			if (!isMountedRef.current) return;
			if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
				setErrorText(err?.message || "No se pudo guardar");
			}
		} finally {
			if (isMountedRef.current) setIsSaving(false);
			abortRef.current = null;
		}
	};

	const handleKeyDown = (event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			persistChange();
		} else if (event.key === "Escape") {
			event.preventDefault();
			cancelEdit();
		}
	};

	if (!isEditing) {
		const textToShow = typeof displayFormatter === "function" ? displayFormatter(value) : value ? String(value) : "—";
		return (
			<div className={`${styles.wrapper} ${className}`}>
				<button type="button" onClick={beginEdit} title={readButtonTitle} className={styles.readButton} disabled={disabled}>
					<span className={styles.readText}>{textToShow}</span>
					<Pencil className={styles.readIcon} aria-hidden="true" />
				</button>
			</div>
		);
	}

	return (
		<div className={`${styles.wrapper} ${className}`}>
			<div className={expandOnEdit ? styles.popover : styles.inlineEdit}>
				<input
					ref={inputRef}
					type="datetime-local"
					value={inputValue || ""}
					onChange={(e) => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
					onBlur={autoSaveOnBlur ? persistChange : undefined}
					disabled={isSaving || disabled}
					className={styles.input}
					aria-label="Seleccionar fecha y hora"
				/>

				<div className={styles.actions}>
					<button
						type="button"
						className={styles.saveButton}
						onMouseDown={(e) => e.preventDefault()}
						onClick={persistChange}
						disabled={isSaving || disabled}
					>
						<Save className={styles.actionIcon} aria-hidden="true" />
						<span>{isSaving ? savingText : saveText}</span>
					</button>

					<button type="button" className={styles.cancelButton} onMouseDown={(e) => e.preventDefault()} onClick={cancelEdit} disabled={isSaving}>
						<X className={styles.actionIcon} aria-hidden="true" />
						<span>{cancelText}</span>
					</button>
				</div>

				{errorText ? <div className={styles.errorText}>{errorText}</div> : null}
			</div>
		</div>
	);
}
